import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { ArrowLeft, Tag, Grid } from 'lucide-react';

const GenresPage: React.FC = () => {
  const navigate = useNavigate();
  const [genres, setGenres] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await gameService.getAvailableGenres();
      setGenres(response.genres);
    } catch (err) {
      setError('Erreur lors du chargement des genres');
      console.error('Genres error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenreClick = (genre: string) => {
    navigate(`/genre/${genre}`);
  };

  const formatGenreName = (genre: string) => {
    return genre
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Erreur
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </button>
            <div className="flex items-center">
              <Grid className="w-6 h-6 text-primary-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Tous les genres
              </h1>
            </div>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400">
            Explorez les jeux par genre
          </p>
        </div>
      </div>

      {/* Genres Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {genres.map((genre, index) => (
            <button
              key={index}
              onClick={() => handleGenreClick(genre)}
              className="group bg-white dark:bg-dark-800 rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600"
            >
              <div className="text-center">
                <div className="bg-primary-100 dark:bg-primary-900 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-200 dark:group-hover:bg-primary-800 transition-colors">
                  <Tag className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {formatGenreName(genre)}
                </h3>
              </div>
            </button>
          ))}
        </div>

        {genres.length === 0 && (
          <div className="text-center py-12">
            <Grid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Aucun genre trouvé
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Les genres ne sont pas encore disponibles
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenresPage;
